import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Option } from '@interfaces/common/option';
import { LinkPartnerCompanyRequest } from '@interfaces/partners/requests/partner-company.request';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';

/**
 * Vincular uma empresa ao parceiro, registrando o **Deal manual** junto (card 2937).
 *
 * Vincular deixou de ser um botão que grava direto: agora abre isto, porque o vínculo passou
 * a carregar um **valor pretendido** — e valor é do deal, não do vínculo. É também por isso
 * que o seletor da aba virou de uma empresa por vez: não existe um valor que sirva para
 * várias empresas de uma vez.
 *
 * A empresa chega **escolhida** e aparece só como texto. Não há consulta de CNPJ aqui, ao
 * contrário da tela Comercial › Registrar Deal: lá o parceiro traz um CNPJ que ainda não é
 * cliente e a empresa nasce na hora; aqui ela já existe, já é cliente da casa, e o que falta
 * é só dizer quanto.
 */
@Component({
  selector: 'app-modal-vincular-empresa',
  templateUrl: './modal-vincular-empresa.component.html',
  styleUrls: ['./modal-vincular-empresa.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
})
export class ModalVincularEmpresaComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  /** Avisa a aba para recarregar as duas listas depois de vincular. */
  @Output() vinculou = new EventEmitter<void>();

  /** Empresa escolhida no seletor da aba. Só exibida — não se troca por aqui. */
  empresa: Option | null = null;

  /**
   * Gravando. Nome próprio porque `isLoading` de `VaultBaseComponent` é o spinner **global**
   * (NgRx): redeclará-lo é erro de compilação, e reaproveitá-lo acenderia o overlay da
   * aplicação inteira por causa de um POST de modal.
   */
  gravando = false;

  /**
   * Moedas aceitas pelo deal.
   *
   * Lista fixa e curta de propósito: o backend valida ISO 4217 de três letras, mas a
   * originação da Legatus opera nestas duas. Um campo livre convidaria a digitar `R$`.
   */
  readonly moedas: Option[] = [
    { label: 'BRL — Real', value: 'BRL' },
    { label: 'USD — Dólar', value: 'USD' },
  ];

  form: FormGroup = this.formBuilder.group({
    amount: [null, [Validators.required, Validators.min(0.01)]],
    currencyCode: ['BRL', [Validators.required]],
  });

  private partnerId = '';

  constructor(
    translateService: TranslateService,
    private formBuilder: FormBuilder,
    private partnersService: PartnersHttpService,
    private toast: ToastHelperService,
    private cdr: ChangeDetectorRef
  ) {
    super(translateService);
  }

  /**
   * Abre a modal para a empresa escolhida.
   *
   * @param partnerId Parceiro que recebe o vínculo.
   * @param empresa Empresa já selecionada na aba — vai fixa, só para conferência.
   */
  async abrir(partnerId: string, empresa: Option): Promise<void> {
    this.partnerId = partnerId;
    this.empresa = empresa;
    this.form.reset({ amount: null, currencyCode: 'BRL' });

    this.cdr.markForCheck();
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'vincular-empresa', false, true);
  }

  isFormValid(): boolean {
    return this.form.valid && !!this.empresa;
  }

  /** Vincula e registra o deal — o backend faz as duas coisas na mesma transação. */
  submitAction(): void {
    if (!this.isFormValid() || this.gravando) return;

    const bruto = this.form.getRawValue();

    const payload: LinkPartnerCompanyRequest = {
      companyId: String(this.empresa?.value ?? ''),
      amount: Number(bruto.amount),
      currencyCode: bruto.currencyCode || 'BRL',
    };

    this.gravando = true;

    // `markForCheck` em todo ponto que mexe em `gravando`: a aba que hospeda esta modal é
    // `OnPush`, e o retorno do HTTP pula a subárvore inteira sem isto — o botão ficaria em
    // "Vinculando..." para sempre depois de um erro.
    this.cdr.markForCheck();

    this.partnersService.linkPartnerCompany(this.partnerId, payload).subscribe({
      next: () => {
        this.gravando = false;
        this.cdr.markForCheck();
        this.toast.showToastSuccess(
          'Empresa vinculada',
          'O deal manual foi registrado e já aparece no board.'
        );
        this.dialogRef?.close();
        this.vinculou.emit();
      },
      error: error => {
        this.gravando = false;
        this.cdr.markForCheck();
        // A mensagem do backend é a que diz o que fazer ("já está com outro parceiro",
        // "parceiro inativo"). Texto genérico só para falha técnica.
        this.toast.showToastError(
          'Erro ao vincular a empresa',
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }
}
